import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable,
  AdminModal,
  ConfirmDialog,
  FormField,
  LangTabs,
  Toggle,
  PublishBadge,
  SortOrderInput,
  LoadingState,
  ErrorState,
  ApiModeBanner,
  inputCls,
} from "../components/AdminUI";
import { useAdminData } from "../../../store/adminDataStore";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import type { PortfolioItem, MediaType, HomepageDisplayMode } from "../../../store/types";

const NOW = () => new Date().toISOString();
const SHOOT_TYPES = ["portrait", "couple", "family", "landscape", "detail", "street"];
const DISPLAY_MODES: HomepageDisplayMode[] = ["reel", "still", "both"];

const EMPTY: PortfolioItem = {
  id: "", title: "", city: "kyoto", shoot_type: "portrait",
  media_type: "image", thumbnail_url: "", media_url: "", cover_image_url: "",
  description_zh: "", description_en: "", description_ja: "",
  photographer_id: "", is_published: false, is_featured: false,
  is_showreel: false, homepage_display_mode: "still",
  sort_order: 99, created_at: NOW(), updated_at: NOW(),
};

export default function AdminPortfolioPage() {
  const { state, upsertPortfolioItem, removePortfolioItem, status, refetchPortfolio, isApiMode } = useAdminData();
  const t = useAdminT();
  const { showToast } = useToast();

  const items = [...state.portfolioItems].sort((a, b) => a.sort_order - b.sort_order);
  const [editing, setEditing] = useState<PortfolioItem | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [lang, setLang] = useState<"zh" | "en" | "ja">("zh");
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "published" | "showreel" | "featured">("all");

  const filtered = items.filter(i => {
    if (filter === "published") return i.is_published;
    if (filter === "showreel") return i.is_showreel;
    if (filter === "featured") return i.is_featured;
    return true;
  });

  const openAdd = () => { setEditing({ ...EMPTY, id: `pf-${Date.now()}` }); setIsNew(true); };
  const openEdit = (item: PortfolioItem) => { setEditing({ ...item }); setIsNew(false); };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await upsertPortfolioItem({ ...editing, updated_at: NOW() });
      showToast(t("tabifotoAdmin.common.save_success"), "success");
      setEditing(null);
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item: PortfolioItem, field: keyof PortfolioItem, value: boolean) => {
    try {
      await upsertPortfolioItem({ ...item, [field]: value, updated_at: NOW() });
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await removePortfolioItem(id);
      showToast(t("tabifotoAdmin.common.delete_success"), "success");
    } catch {
      showToast(t("tabifotoAdmin.common.delete_error"), "error");
    }
    setConfirmDel(null);
  };

  const set = <K extends keyof PortfolioItem>(k: K, v: PortfolioItem[K]) => {
    if (!editing) return;
    setEditing({ ...editing, [k]: v });
  };

  const BADGE_MODE: Record<HomepageDisplayMode, string> = {
    reel: "bg-[#C8B8A6]/20 text-[#C8B8A6]",
    still: "bg-[#D9D9D9]/60 text-[#2C2C2C]/50",
    both: "bg-emerald-50 text-emerald-600",
  };

  const FILTER_KEYS = ["all", "published", "showreel", "featured"] as const;
  const filterLabel: Record<string, string> = {
    all: t("tabifotoAdmin.portfolio.filter_all"),
    published: t("tabifotoAdmin.portfolio.filter_published"),
    showreel: t("tabifotoAdmin.portfolio.filter_showreel"),
    featured: t("tabifotoAdmin.portfolio.filter_featured"),
  };
  const filterCount: Record<string, number> = {
    all: items.length,
    published: items.filter(i => i.is_published).length,
    showreel: items.filter(i => i.is_showreel).length,
    featured: items.filter(i => i.is_featured).length,
  };

  const HEADERS = [
    t("tabifotoAdmin.portfolio.col_order"),
    t("tabifotoAdmin.portfolio.col_thumb"),
    t("tabifotoAdmin.portfolio.col_title"),
    t("tabifotoAdmin.portfolio.col_city"),
    t("tabifotoAdmin.portfolio.col_shoot_type"),
    t("tabifotoAdmin.portfolio.col_showreel"),
    t("tabifotoAdmin.portfolio.col_featured"),
    t("tabifotoAdmin.portfolio.col_published"),
    t("tabifotoAdmin.portfolio.col_display_mode"),
    t("tabifotoAdmin.portfolio.col_sort"),
    t("tabifotoAdmin.portfolio.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.portfolio.title")}
      subtitle={t("tabifotoAdmin.portfolio.subtitle")}
      action={
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#2C2C2C] text-white text-sm hover:bg-[#2C2C2C]/80 cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
          {t("tabifotoAdmin.portfolio.add_item")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      {status.portfolio.loading && <LoadingState />}
      {status.portfolio.error && <ErrorState message={status.portfolio.error} onRetry={refetchPortfolio} />}

      {!status.portfolio.loading && !status.portfolio.error && (
        <>
          <div className="flex gap-2 mb-4">
            {FILTER_KEYS.map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-light cursor-pointer whitespace-nowrap transition-colors ${filter === f ? "bg-[#2C2C2C] text-white" : "bg-white border border-[#D9D9D9] text-[#2C2C2C]/60 hover:border-[#2C2C2C]/30"}`}>
                {filterLabel[f]} <span className="opacity-40 ml-1">{filterCount[f]}</span>
              </button>
            ))}
          </div>

          <AdminTable headers={HEADERS} empty={filtered.length === 0} emptyMessage={t("tabifotoAdmin.portfolio.no_items")}>
            {filtered.map(item => (
              <tr key={item.id} className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60">
                <td className="px-4 py-3 text-xs text-[#2C2C2C]/30">{item.sort_order}</td>
                <td className="px-4 py-3">
                  <div className="w-16 h-10 rounded overflow-hidden bg-[#D9D9D9]/30">
                    {item.thumbnail_url && <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-light text-[#2C2C2C] max-w-[140px] truncate">{item.title}</td>
                <td className="px-4 py-3"><span className="text-xs bg-[#C8B8A6]/20 text-[#C8B8A6] px-2 py-0.5 rounded-full capitalize">{item.city}</span></td>
                <td className="px-4 py-3 text-xs text-[#2C2C2C]/40 capitalize">{item.shoot_type}</td>
                <td className="px-4 py-3">
                  <Toggle checked={item.is_showreel} onChange={v => handleToggle(item, "is_showreel", v)} />
                </td>
                <td className="px-4 py-3">
                  <Toggle checked={item.is_featured} onChange={v => handleToggle(item, "is_featured", v)} />
                </td>
                <td className="px-4 py-3">
                  <PublishBadge published={item.is_published} />
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-light ${BADGE_MODE[item.homepage_display_mode]}`}>
                    {item.homepage_display_mode}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <SortOrderInput order={item.sort_order} onChange={v => upsertPortfolioItem({ ...item, sort_order: v, updated_at: NOW() })} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(item)} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] cursor-pointer"><i className="ri-edit-line" /></button>
                    <button onClick={() => setConfirmDel(item.id)} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/30 hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        </>
      )}

      {editing && (
        <AdminModal
          title={isNew ? t("tabifotoAdmin.portfolio.add_item") : `${t("tabifotoAdmin.portfolio.edit_item")}: ${editing.title}`}
          onClose={() => setEditing(null)} onSave={handleSave} saving={saving}
        >
          <div className="grid grid-cols-2 gap-3 mb-3">
            <FormField label={t("tabifotoAdmin.portfolio.title_field")} required>
              <input className={inputCls} value={editing.title} onChange={e => set("title", e.target.value)} />
            </FormField>
            <FormField label={t("tabifotoAdmin.portfolio.photographer_id")}>
              <input className={inputCls} value={editing.photographer_id} onChange={e => set("photographer_id", e.target.value)} placeholder="ph-001" />
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <FormField label={t("tabifotoAdmin.portfolio.city")}>
              <select className={inputCls} value={editing.city} onChange={e => set("city", e.target.value)}>
                {["kyoto", "osaka", "tokyo"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label={t("tabifotoAdmin.portfolio.shoot_type")}>
              <select className={inputCls} value={editing.shoot_type} onChange={e => set("shoot_type", e.target.value)}>
                {SHOOT_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </FormField>
            <FormField label={t("tabifotoAdmin.portfolio.media_type")}>
              <select className={inputCls} value={editing.media_type} onChange={e => set("media_type", e.target.value as MediaType)}>
                <option value="image">image</option>
                <option value="video">video</option>
              </select>
            </FormField>
          </div>
          <FormField label={t("tabifotoAdmin.portfolio.thumbnail_url")}>
            <input className={inputCls} value={editing.thumbnail_url} onChange={e => set("thumbnail_url", e.target.value)} placeholder="https://..." />
            {editing.thumbnail_url && (
              <div className="mt-2 w-full h-24 rounded overflow-hidden bg-[#D9D9D9]/20">
                <img src={editing.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
          </FormField>
          <FormField label={t("tabifotoAdmin.portfolio.media_url")}>
            <input className={inputCls} value={editing.media_url} onChange={e => set("media_url", e.target.value)} placeholder="https://..." />
          </FormField>
          <LangTabs activeLang={lang} onChange={setLang} />
          <div className="mt-3">
            <FormField label={`${t("tabifotoAdmin.portfolio.description")} (${lang})`}>
              <textarea className={`${inputCls} resize-none`} rows={3} maxLength={500}
                value={(editing as Record<string, unknown>)[`description_${lang}`] as string}
                onChange={e => set(`description_${lang}` as keyof PortfolioItem, e.target.value as never)} />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <FormField label={t("tabifotoAdmin.portfolio.display_mode")}>
              <select className={inputCls} value={editing.homepage_display_mode} onChange={e => set("homepage_display_mode", e.target.value as HomepageDisplayMode)}>
                {DISPLAY_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </FormField>
            <FormField label={t("tabifotoAdmin.common.sort_order")}>
              <input type="number" className={inputCls} value={editing.sort_order} onChange={e => set("sort_order", Number(e.target.value))} />
            </FormField>
          </div>
          <div className="flex gap-5 mt-2">
            {(["is_published", "is_featured", "is_showreel"] as const).map(field => (
              <label key={field} className="flex items-center gap-2 text-xs text-[#2C2C2C]/60 cursor-pointer">
                <Toggle checked={editing[field] as boolean} onChange={v => set(field, v as never)} />
                {field}
              </label>
            ))}
          </div>
        </AdminModal>
      )}

      {confirmDel && (
        <ConfirmDialog
          title={t("tabifotoAdmin.portfolio.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")}
          confirmLabel={t("tabifotoAdmin.common.delete")}
          danger
          onConfirm={() => handleDelete(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </AdminLayout>
  );
}
